import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerSubmissionListParams,
  type BuyerSubmissionStatus,
  type BuyerSubmissionSummaryDto,
} from "@/api/buyers";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  buttonVariants,
  ColumnChooser,
  EmptyState,
  ExceptionBadge,
  FilterChips,
  ListToolbar,
  MissingValue,
  ScopeToggle,
  Skeleton,
  SortSelect,
  StatCard,
  StatsBar,
  StatusBadge,
  SubTable,
  SUB_TABLE_CHROME_THRESHOLD,
  Timestamp,
  Tooltip,
  type ActiveFilterChip,
  type StatusTone,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { listingNameOrFallback, listingShortNameOrFallback } from "@/lib/listingDisplay";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import { useAuth } from "@/hooks/useAuth";
import { useListFilters } from "@/hooks/useListFilters";
import {
  anyFilterActive,
  resolveSort,
  SUBMISSION_SORTS,
  buyerLabel,
  type SubmissionCardFilter,
} from "./buyerFilters";
import { BuyerPager } from "./BuyerPager";
import { submissionStatusTone } from "./buyerListBits";
import { submissionPhaseLadder } from "./submissionPhases";
import {
  defaultSubmissionView,
  SUBMISSION_ACTION_REASONS,
  SUBMISSION_LIST_FILTERS,
  SUBMISSION_STATUS_GROUPS,
  SUBMISSION_VIEWS,
  submissionNeedsActionReasons,
  type SubmissionNeedsActionReason,
} from "./submissionFilters";
import { PresentBuyerWizard } from "./presentBuyer/PresentBuyerWizard";
import { WhosCallingButton } from "@/components/buyers/WhosCallingPanel";
import {
  MY_SCOPE_PROBE,
  MyScopeEmptyState,
  SUBMISSIONS_SCOPE_PREFERENCE,
  useRememberedScope,
} from "./listScope";
import { QueryErrorState } from "./queryStates";
import {
  AGENT_CHOOSER_COLUMNS,
  DEFAULT_OFF_AGENT_COLUMNS,
  listingAgentColumns,
  useColumnsWithDefaultOff,
} from "./listingAgentColumn";
import {
  ShowArchivedToggle,
  SubmissionRowMenu,
  useSubmissionRemoval,
  withArchivedRows,
  type SubmissionRemoval,
} from "./submission/submissionArchive";

/** Per-user preference keys (Q7). */
const VIEW_PREFERENCE = "submissions-list-view";
const COLUMNS_PREFERENCE = "submissions-list-columns";

/** The columns the reader may hide. The two identity columns and the way in
 *  are locked — a row that cannot be opened is not a row. */
const CHOOSABLE_COLUMNS = [
  { key: "buyer", label: "Buyer", locked: true },
  { key: "listing", label: "Listing", locked: true },
  // Addendum B (B1): the agent, and two contact columns OFF by default.
  ...AGENT_CHOOSER_COLUMNS,
  { key: "status", label: "Status" },
  { key: "asking", label: "Asking" },
  { key: "offer", label: "Deal structure price" },
  { key: "conversations", label: "Conversations" },
  { key: "follow-up", label: "Follow-up" },
  { key: "open", label: "Open", locked: true },
];

/** The needs-action reasons as the badge vocabulary (§6): red for a breached
 *  date, amber for something waiting on somebody, muted for a step to take. */
function reasonBadge(reason: SubmissionNeedsActionReason): { label: string; tone: StatusTone } {
  switch (reason) {
    case "FollowUpOverdue":
      return { label: "Follow-up overdue", tone: "danger" };
    case "AwaitingBroker":
      return { label: "Awaiting broker", tone: "warn" };
    case "NoActivity":
      return { label: "No activity", tone: "warn" };
    case "PackageNotSent":
      return { label: "Package not sent", tone: "neutral" };
    case "NdaOutstanding":
      return { label: "NDA outstanding", tone: "neutral" };
    default:
      return { label: reason, tone: "neutral" };
  }
}

/** The server's page size — and what tells `SubTable` it can never hold enough
 *  rows to earn chrome of its own. See the note at the table. */
const PAGE_SIZE = 25;

/**
 * The list request as the server actually accepts it.
 *
 * <p>🔴 <c>sortBy</c>/<c>sortDirection</c> live on <c>PagedQuery</c> and are read
 * by <c>BuyerSubmissionService.ApplySort</c>; the shared client mirror never
 * declared them, which is the only reason this list has had no sort control.
 * They belong on <c>BuyerSubmissionListParams</c> — declared here because
 * <c>api/buyers.ts</c> is outside this change's scope.</p>
 */
type SubmissionListRequest = BuyerSubmissionListParams & {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
};

/** The statuses a stage-bar segment stands for. An unrecognised rung filters
 *  nothing, so a stale bookmark degrades to the unfiltered view rather than to
 *  an empty one. */
function statusesForPhase(phase: string): BuyerSubmissionStatus[] | null {
  if (!phase) return null;
  const rung = submissionPhaseLadder.phases.find((p) => p.key === phase);
  return rung ? [...rung.statuses] : null;
}

/**
 * Submissions — every buyer↔listing pairing and where its negotiation stands.
 *
 * The cross-cutting view: a specialist's whole book of live conversations in
 * one place, rather than one buyer at a time. `?buyerLeadId=` and
 * `?listingId=` narrow it for the deep links from those two records.
 */
export function BuyerSubmissionsListPage() {
  const filters = useListFilters(SUBMISSION_LIST_FILTERS);
  const { values, page } = filters;
  const { hasPermission, hasRoleCode } = useAuth();
  const [creating, setCreating] = useState(false);

  // Q7 — the last view and the hidden columns, per user.
  const viewPref = useUserPreference<string>(VIEW_PREFERENCE, "");
  const { hiddenKeys: hiddenColumnKeys, onChange: onColumnsChange } = useColumnsWithDefaultOff(
    COLUMNS_PREFERENCE,
    DEFAULT_OFF_AGENT_COLUMNS,
  );

  // B5 — My by default, then the reader's last choice; a link's ?scope= wins.
  const scopeChoice = useRememberedScope(SUBMISSIONS_SCOPE_PREFERENCE, values.scope, (next) =>
    filters.set("scope", next),
  );

  // URL choice → persisted preference → the role's default. `view` is never
  // written to the URL at its default, so a bare `/buyer-submissions` resolves
  // through the preference and a shared `?view=` link opens the same rows for
  // anyone.
  const activeView =
    values.view ||
    (SUBMISSION_VIEWS.some((v) => v.key === viewPref.value) ? viewPref.value : "") ||
    defaultSubmissionView(hasRoleCode("F2"));
  const view = SUBMISSION_VIEWS.find((v) => v.key === activeView) ?? SUBMISSION_VIEWS[SUBMISSION_VIEWS.length - 1];
  const needsActionView = activeView === "needs-action";

  const selectView = (key: string) => {
    filters.set("view", key);
    viewPref.set(key);
  };

  // The reason chip in force inside the "Needs action" view — one of the four
  // attention pills (`card`) or the follow-up flag (`overdue`). Empty means the
  // union, which the page computes over the rows in hand (below).
  const reasonKey = values.overdue ? "overdue" : values.card;
  const reason = SUBMISSION_ACTION_REASONS.find((r) => r.key === reasonKey);

  // Effective sort: an explicit choice on the control wins; otherwise the view
  // says how it opens; otherwise the descriptor's default.
  const sortToken =
    values.sort !== SUBMISSION_SORTS[0].value ? values.sort : (view.sort ?? SUBMISSION_SORTS[0].value);

  const params = useMemo<SubmissionListRequest>(
    () => ({
      // The view's own params sit UNDER everything explicit.
      ...view.params,
      page,
      pageSize: PAGE_SIZE,
      ...resolveSort(SUBMISSION_SORTS, sortToken),
      q: values.q || undefined,
      // The status dropdown wins over a stage-bar segment, which wins over the
      // view — each is more specific than the one after it.
      status: values.status
        ? [values.status]
        : (statusesForPhase(values.phase) ?? view.params.status ?? undefined),
      liveOnly: values.liveOnly || view.params.liveOnly ? true : undefined,
      mineOnly: scopeChoice.scope === "mine" ? true : undefined,
      buyerLeadId: values.buyerLeadId,
      listingId: values.listingId,
      // D4 — archived rows only when Show archived is on; the stats follow.
      includeArchived: values.archived ? true : undefined,
      // ONE attention reason at a time, riding the SAME server-side expression
      // its count was computed with, so a chip and this table cannot disagree.
      ...(reason?.params ?? {}),
    }),
    [page, values, view, sortToken, reason, scopeChoice.scope],
  );

  const list = useQuery({
    queryKey: buyerSubmissionKeys.list(params),
    queryFn: () => buyerSubmissionsApi.list(params),
    // Wait for the persisted view and scope: firing with the fallback and again
    // with the preference would be the double-fetch class, one render apart.
    enabled: !viewPref.isLoading && !scopeChoice.isLoading,
  });

  // The SAME params as the table, minus paging — so the cards can never
  // describe a different set of rows than the list below them. The StatCards
  // stay read-only: `liveOnly` and `overdue` are already controls, and a
  // clickable card would be a second way to say the same thing.
  //
  // The attention reasons are dropped here, as the buyers page drops its card
  // filter: applying a reason to its own counts would collapse every other
  // reason's count to zero the moment one was clicked. A stage-bar segment
  // rides `status`, which the server already excludes from its own breakdown,
  // so it is dropped for free.
  const statsParams = useMemo<SubmissionListRequest>(
    () => ({
      ...params,
      page: 1,
      // Ordering cannot change a count — dropped so choosing a sort does not
      // re-key the stats query and refetch every tile for nothing.
      sortBy: undefined,
      sortDirection: undefined,
      overdueFollowUpOnly: undefined,
      awaitingBrokerResponseDays: undefined,
      packageGeneratedNotSentOnly: undefined,
      ndaOutstandingOnly: undefined,
      noActivityWithinDays: undefined,
    }),
    [params],
  );
  const stats = useQuery({
    queryKey: buyerSubmissionKeys.stats(statsParams),
    queryFn: () => buyerSubmissionsApi.stats(statsParams),
    enabled: !scopeChoice.isLoading,
  });

  // 🔴 Memoised on the query result rather than written inline — `?? []` in a
  // render body allocates a fresh array every render while the query is in
  // flight (see `buyerSelection.ts` for what that cost once).
  const fetched = useMemo(() => list.data?.data ?? [], [list.data]);
  const total = list.data?.totalCount ?? 0;

  // D11 — why each row needs the specialist, computed once per result set.
  const reasonsById = useMemo(
    () => new Map(fetched.map((s) => [s.id, submissionNeedsActionReasons(s)])),
    [fetched],
  );
  // The "Needs action" view with no reason chip chosen is the UNION of the
  // five reasons — which the server cannot express, so it is taken over the
  // page in hand and said so beside the table. With a chip chosen the server
  // already narrowed the set and every row it sent stands.
  const narrowsClientSide = needsActionView && !reason;
  const rows = useMemo(
    () =>
      narrowsClientSide
        ? fetched.filter((s) => (reasonsById.get(s.id)?.length ?? 0) > 0)
        : fetched,
    [fetched, narrowsClientSide, reasonsById],
  );

  // B5 — whether an empty "My" view means the reader has no submissions at
  // all, asked only when it is empty.
  const myProbe = useQuery({
    queryKey: buyerSubmissionKeys.list(MY_SCOPE_PROBE),
    queryFn: () => buyerSubmissionsApi.list(MY_SCOPE_PROBE),
    enabled: scopeChoice.scope === "mine" && list.isSuccess && rows.length === 0,
  });

  const scoped = Boolean(values.buyerLeadId || values.listingId);
  const canManage = hasPermission("Submissions.Manage");

  /**
   * §5 — search, sort and the footer arrive together at ten rows and not before.
   *
   * 🔴 Keyed on the whole result set plus "has the reader narrowed anything",
   * never on the rows currently rendered: a book filtered down to three rows
   * must keep the controls that got the reader there. A deep link from a buyer
   * or a listing counts as narrowing (`buyerLeadId`/`listingId` are declared
   * filters), so a one-buyer view keeps its way back out.
   */
  const hasChrome = anyFilterActive(filters) || total >= SUB_TABLE_CHROME_THRESHOLD;

  const removal = useSubmissionRemoval({ canDeleteAtAll: hasPermission("Submissions.Delete") });
  const columns = withArchivedRows(
    buildSubmissionColumns(needsActionView ? reasonsById : null, removal),
    // The pill rides the LOCKED buyer column, so hiding Status never hides it.
    { pillColumnKey: "buyer", keepKeys: ["open"] },
  );

  // Row 3 of the toolbar — everything narrowing the list that is not visible at
  // rest. Search, scope and the reason chips are on screen; these are behind
  // "More filters".
  const activeChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (values.status)
      chips.push({ key: "status", label: `Status: ${buyerLabel(values.status)}`, onRemove: () => filters.set("status", "") });
    if (values.liveOnly && !view.params.liveOnly)
      chips.push({ key: "liveOnly", label: "Live only", onRemove: () => filters.set("liveOnly", false) });
    if (values.overdue && !needsActionView)
      chips.push({ key: "overdue", label: "Overdue follow-up", onRemove: () => filters.set("overdue", false) });
    return chips;
  }, [values.status, values.liveOnly, values.overdue, view, needsActionView, filters]);

  const byStatus = stats.data?.byStatus ?? {};

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title="Submissions"
        description="Every buyer presented to a listing, and how far each conversation has got."
        actions={
          // The header's actions slot already lays these out (wrap + gap).
          <>
            <WhosCallingButton />
            {canManage && (
              <Button data-testid="submission-create" onClick={() => setCreating(true)}>
                Present a buyer
              </Button>
            )}
          </>
        }
      />

      <StatsBar columns={5} data-testid="submission-stats">
        <StatCard label="Total" value={stats.data?.total ?? "—"} />
        <StatCard label="Live" value={stats.data?.live ?? "—"} />
        <StatCard label="Under contract" value={stats.data?.underContract ?? "—"} />
        <StatCard label="Overdue follow-up" value={stats.data?.overdueFollowUp ?? "—"} />
        <StatCard label="Closed out" value={stats.data?.terminal ?? "—"} />
      </StatsBar>

      {/* A scoped view is easy to arrive at from a deep link and hard to notice
          afterwards — an "empty" list that is actually filtered to one record
          reads as no data at all. Say so, and offer the way out. */}
      {scoped && (
        <div className="flex items-center gap-space-3 mb-space-3 text-small">
          <span className="text-gray-mid">
            Showing submissions for one {values.buyerLeadId ? "buyer" : "listing"}.
          </span>
          <button
            type="button"
            className="text-gold-shadow hover:underline"
            onClick={() => {
              filters.set("buyerLeadId", undefined);
              filters.set("listingId", undefined);
            }}
          >
            Show all submissions
          </button>
        </div>
      )}

      {/* §3 — one band: search | lens | sort; views | reason chips | More
          filters | columns; the applied chips. Search and sort are §5-gated. */}
      <ListToolbar
        data-testid="submissions-toolbar"
        search={
          hasChrome
            ? {
                value: filters.inputs.q,
                onChange: (next) => filters.set("q", next),
                placeholder: "Search buyer, listing or agent (name, email, phone)…",
                label: "Search submissions",
              }
            : undefined
        }
        lens={
          <ScopeToggle
            value={scopeChoice.scope}
            onChange={scopeChoice.choose}
            mineLabel="My submissions"
            allLabel="All submissions"
            label="Submission view"
            data-testid="scope-toggle"
          />
        }
        sort={
          hasChrome ? (
            <SortSelect
              value={sortToken}
              onChange={(next) => filters.set("sort", next)}
              options={SUBMISSION_SORTS}
              label="Sort submissions by"
            />
          ) : undefined
        }
        views={{
          items: SUBMISSION_VIEWS.map((v) => ({
            key: v.key,
            label: v.label,
            badge:
              v.key === "live"
                ? stats.data?.live
                : v.key === "under-contract"
                  ? stats.data?.underContract
                  : v.key === "closed-out"
                    ? stats.data?.terminal
                    : undefined,
          })),
          activeKey: activeView,
          onSelect: selectView,
          label: "Submission views",
        }}
        primary={
          needsActionView ? (
            <FilterChips
              label="Action reason"
              activeId={reasonKey}
              onChange={(key) => {
                // The reason rides ONE of two params, so choosing one clears the
                // other in the same write — never two sets in a row.
                const next = key === reasonKey ? "" : key;
                filters.set("card", (next === "overdue" ? "" : next) as SubmissionCardFilter);
                filters.set("overdue", next === "overdue");
              }}
              chips={[
                { id: "", label: "Any reason" },
                ...SUBMISSION_ACTION_REASONS.map((r) => {
                  const count =
                    r.key === "overdue"
                      ? stats.data?.overdueFollowUp
                      : r.key === "awaiting-broker"
                        ? stats.data?.awaitingBrokerResponse
                        : r.key === "package-not-sent"
                          ? stats.data?.packageGeneratedNotSent
                          : r.key === "nda-outstanding"
                            ? stats.data?.ndaOutstanding
                            : stats.data?.noRecentActivity;
                  return { id: r.key, label: count == null ? r.label : `${r.label} (${count})` };
                }),
              ]}
            />
          ) : undefined
        }
        moreFilters={{
          activeCount: activeChips.length,
          content: (
            <div className="flex flex-col gap-space-3">
              {/* The twenty statuses, grouped by the SAME six rungs the detail
                  stepper renders plus the five that ended. Single-select, on
                  the same `?status=` the dropdown wrote. */}
              {SUBMISSION_STATUS_GROUPS.map((group) => (
                <div key={group.key} className="flex flex-col gap-space-1">
                  <span className="text-caption text-gray-mid">{group.label}</span>
                  <FilterChips
                    label={`${group.label} statuses`}
                    activeId={values.status}
                    onChange={(id) => filters.set("status", (id === values.status ? "" : id) as BuyerSubmissionStatus | "")}
                    chips={group.statuses.map((st) => {
                      const count = byStatus[st];
                      return { id: st, label: count == null ? buyerLabel(st) : `${buyerLabel(st)} (${count})` };
                    })}
                  />
                </div>
              ))}
              <div className="flex flex-col gap-space-1">
                <span className="text-caption text-gray-mid">Lifecycle</span>
                <FilterChips
                  label="Lifecycle filter"
                  activeId={values.liveOnly ? "1" : ""}
                  onChange={(id) => filters.set("liveOnly", id === "1")}
                  chips={[
                    { id: "", label: "All submissions" },
                    { id: "1", label: "Live only" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-space-1">
                <span className="text-caption text-gray-mid">Follow-up</span>
                <FilterChips
                  label="Follow-up filter"
                  activeId={values.overdue ? "1" : ""}
                  onChange={(id) => filters.set("overdue", id === "1")}
                  chips={[
                    { id: "", label: "Any follow-up" },
                    { id: "1", label: "Overdue only" },
                  ]}
                />
              </div>
            </div>
          ),
        }}
        activeChips={activeChips}
        onClearAllChips={() => {
          // ONE write — `set` builds its next params from THIS render's, so a
          // chain of sets would have the last overwrite the rest.
          filters.reset();
        }}
        actions={
          <div className="flex flex-wrap items-center gap-space-3">
            <ShowArchivedToggle
              checked={values.archived}
              onChange={(next) => filters.set("archived", next)}
            />
            <ColumnChooser
              columns={CHOOSABLE_COLUMNS}
              hiddenKeys={hiddenColumnKeys}
              onChange={onColumnsChange}
              data-testid="submission-columns"
            />
          </div>
        }
      />

      {/* The conversation-stage, volume, attention and velocity blocks moved to
          the Reports page (WS-B4); a list is for working rows, not for reading
          the book. */}
      <p className="mb-space-3 text-caption text-gray-mid" data-testid="submissions-reports-link">
        Conversation stage, submissions started and velocity are on{" "}
        <Link to="/buyers/reports?tab=submissions" className="text-gold-shadow hover:underline">
          Reports
        </Link>
        .
      </p>

      {/* The union is taken over the page in hand — the server has no single
          expression for it — so say how many of the rows fetched it kept. */}
      {narrowsClientSide && !list.isLoading && !list.isError && (
        <p className="mb-space-2 text-caption text-gray-mid" role="status" data-testid="needs-action-caption">
          {rows.length} of the {fetched.length} live submissions on this page need action.
          Pick a reason above to see the server's full set for it.
        </p>
      )}

      {list.isLoading || viewPref.isLoading || scopeChoice.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <QueryErrorState
          what="the submissions list"
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 && scopeChoice.scope === "mine" ? (
        <MyScopeEmptyState
          noun="submissions"
          nothingYet={myProbe.data?.totalCount === 0}
          onShowAll={() => scopeChoice.choose("all")}
        />
      ) : rows.length === 0 ? (
        /* The copy was unconditional while the button that satisfies it is
           `canManage`-gated, so a read-only specialist was told to do something
           this page does not offer them. Branch it, per ListingsListPage. */
        <EmptyState
          title="No submissions match"
          description={
            canManage
              ? "Widen the filters, or present a buyer to a listing to create one."
              : "Widen the filters to see more."
          }
        />
      ) : (
        /*
          `SubTable` picks the header variant from the surface it is mounted in
          (§4.3) and owns §5's threshold. `chromeThreshold={PAGE_SIZE}` puts that
          threshold permanently out of reach, because its search / sort / footer
          act on the ARRAY IT HOLDS — one server page — and would each claim a
          reach they do not have. The page owns all three, server-side.
        */
        <SubTable<BuyerSubmissionSummaryDto>
          caption="Submissions"
          columns={columns}
          rows={rows}
          rowKey={(s) => s.id}
          chromeThreshold={PAGE_SIZE}
          hiddenColumnKeys={hiddenColumnKeys}
          stickyHeader
          data-testid="submissions-table"
        />
      )}

      <BuyerPager
        filters={filters}
        total={total}
        shown={rows.length}
        pageSize={PAGE_SIZE}
        hidden={!hasChrome}
      />

      {/*
        The one entry point where NEITHER half is known — deliberately left
        unprefilled even when the list is scoped by `?buyerLeadId=` /
        `?listingId=`. A filter is a view, not a launch context: locking the
        wizard to whatever the table happens to be narrowed to would put a
        record in the specialist's hands that they only meant to look at.
      */}
      <PresentBuyerWizard open={creating} onClose={() => setCreating(false)} />
      {removal.node}
    </>
  );
}

/**
 * The row, as columns.
 *
 * 🔴 EVERY RICH DETAIL SURVIVES THE CONVERSION. A submission row is a pairing
 * rather than a record with a name, so it carries more than a simpler list does
 * and needs to: the two-line listing cell (short name, whole name on hover, city
 * and state under it), the overdue mark, and the third "Open" affordance below.
 * Adopting the shared header and the shared footer is about how the table LOOKS
 * and when it grows controls — not a licence to flatten what a row says.
 *
 * Rebuilt per render since the row ••• (D3) closes over the archive/delete
 * flows, whose handlers are per-render closures. Nothing downstream keys an
 * effect on the columns' identity, so that costs a cheap array, not a refetch.
 *
 * `reasons` is null outside the "Needs action" view — the badges (D11) belong
 * to that view, where the row's job is to say why it is there.
 */
function buildSubmissionColumns(
  reasons: Map<string, SubmissionNeedsActionReason[]> | null,
  /** The row's ••• — Archive / Restore, and Delete for an admin (D3). */
  removal: SubmissionRemoval,
): SubTableColumn<BuyerSubmissionSummaryDto>[] {
  return [
  {
    key: "buyer",
    header: "Buyer",
    render: (submission) => {
      const rowReasons = reasons?.get(submission.id) ?? [];
      return (
        <div className="min-w-0">
          <Link
            to={`/buyers/${submission.buyerLeadId}`}
            className="text-gold-shadow hover:underline"
          >
            {submission.buyerStaffDisplayName ?? submission.buyerDisplayName}
          </Link>
          {rowReasons.length > 0 && (
            <div
              className="mt-space-1 flex flex-wrap gap-space-1"
              data-testid={`needs-action-${submission.id}`}
            >
              {rowReasons.map((r) => {
                const badge = reasonBadge(r);
                return (
                  <StatusBadge key={r} tone={badge.tone}>
                    {badge.label}
                  </StatusBadge>
                );
              })}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: "listing",
    header: "Listing",
    render: (submission) => {
      const full = listingNameOrFallback(
        submission.listingDisplayName,
        submission.listingBusinessName,
      );
      const short = listingShortNameOrFallback(
        submission.listingDisplayName,
        submission.listingBusinessName,
      );
      return (
        <div className="min-w-0">
          {/*
            SHORT in the cell, whole on hover AND ON KEYBOARD FOCUS.

            🔴 This used to be a native `title`, which §11.4 forbids outright:
            `title` never opens on focus, so a keyboard reader could reach the
            link and still be told nothing about the name it had cut short. It
            also cannot be styled, positioned or dismissed. `Tooltip` clones its
            single child and merges the handlers onto it, so it adds no DOM node
            and cannot perturb the row.

            `aria-label` carries the full name too, because a TOUCH user has no
            hover and may never focus the link — the accessible name is the one
            channel that needs no pointer at all. The visible text stays short.
          */}
          <Tooltip label={full}>
            <Link
              to={`/listings/${submission.listingId}`}
              className="text-gold-shadow hover:underline"
              aria-label={full === short ? undefined : full}
            >
              {short}
            </Link>
          </Tooltip>
          <div className="text-caption text-gray-mid">
            {[submission.listingCity, submission.listingState].filter(Boolean).join(", ")}
          </div>
        </div>
      );
    },
  },
  ...listingAgentColumns<BuyerSubmissionSummaryDto>((s) => s.listingAgent, { withContactColumns: true }),
  {
    key: "status",
    header: "Status",
    render: (submission) => (
      <StatusBadge tone={submissionStatusTone(submission.status)}>
        {buyerLabel(submission.status)}
      </StatusBadge>
    ),
  },
  {
    key: "asking",
    header: "Asking",
    align: "right",
    // §7 — a plain missing value, with no door: an asking price is the
    // marketplace's fact and is corrected on the listing, not from here.
    render: (submission) => (
      <TableValueMoney value={submission.listingAskingPrice} />
    ),
  },
  {
    // The key stays `offer`: it is in saved column preferences.
    key: "offer",
    header: "Deal structure price",
    align: "right",
    render: (submission) => <TableValueMoney value={submission.dealStructurePrice} />,
  },
  {
    key: "conversations",
    header: "Conversations",
    align: "right",
    render: (submission) => submission.conversationCount,
  },
  {
    key: "follow-up",
    header: "Follow-up",
    render: (submission) => (
      <>
        {submission.nextFollowUpDate ? (
          <span className={submission.isFollowUpOverdue ? "text-status-error" : undefined}>
            <Timestamp value={submission.nextFollowUpDate} />
          </span>
        ) : (
          <MissingValue />
        )}
        {/* Overdue is computed SERVER-side so this mark, the filter and any
            badge count can never disagree about the cutoff. §6.4 — red is for
            a breached date, amber for something that merely wants a look. */}
        {submission.isFollowUpOverdue && (
          <ExceptionBadge severity="overdue" className="ml-0 mt-space-1 flex w-fit">
            Overdue
          </ExceptionBadge>
        )}
      </>
    ),
  },
  {
    /*
      🔴 THE WAY IN. Until this cell existed, the global submissions list held
      no link to a submission at all: both name cells are spent on the two
      FOREIGN KEYS — the buyer and the listing — so the row had nothing left
      to point at itself with, and a specialist reading their whole book of
      conversations could open either party to the conversation but never the
      conversation.

      That is why this is a THIRD affordance rather than a repair to one of
      the existing two. On Buyers and Listings the name cell IS the record;
      here the record has no name of its own — it is `{buyer} → {listing}`,
      which is exactly why both halves were already spoken for. It survives the
      conversion to `SubTable` as its own column for the same reason.

      It NAVIGATES rather than opening a drawer. Every record drawer in this
      module hangs off a parent record whose page stays underneath; the global
      index has no parent, so there would be nothing behind the drawer to
      return to.

      An anchor, not a button: middle-click, "open in new tab" and copy-link
      all work on the first and none of them work on the second. Styled with
      `buttonVariants` rather than a retyped class string, so a change to the
      button's focus treatment reaches it too.
    */
    key: "open",
    header: "Open",
    className: "w-[1%]",
    render: (submission) => (
      <div className="flex items-center justify-end gap-space-2">
        <Link
          to={`/buyer-submissions/${submission.id}`}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
          aria-label={`Open the submission for ${submission.buyerDisplayName}`}
          data-testid="submission-open"
        >
          Open
        </Link>
        <SubmissionRowMenu
          removal={removal}
          target={{
            id: submission.id,
            listingName: listingNameOrFallback(
              submission.listingDisplayName, submission.listingBusinessName),
            isArchived: submission.isArchived,
            canArchive: submission.canArchive,
            archiveBlockedReason: submission.archiveBlockedReason,
            canDelete: submission.canDelete,
            deleteBlockedReason: submission.deleteBlockedReason,
          }}
        />
      </div>
    ),
  },
  ];
}

/** Currency, or the §7 em dash. One place, so the two money columns cannot
 *  disagree about what "no figure" looks like. */
function TableValueMoney({ value }: { value?: number | null }) {
  return value == null ? <MissingValue /> : <>{formatCurrency(value)}</>;
}
